import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaChevronDown, FaChevronUp, FaPlus, FaTrash, FaSearch, FaTimes, FaExclamationTriangle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const PayrollSchemeSelectEmployees = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  function getFinYearId() {
    try {
      const direct = localStorage.getItem("finYearId");
      if (direct) return String(direct);
      const admin = JSON.parse(localStorage.getItem("admin") || "null");
      if (admin?.finYearId) return String(admin.finYearId);
    } catch (_) {}
    return "";
  }

  function tenantHeaders() {
    const fy = getFinYearId();
    return fy ? { "x-fin-year-id": fy } : {};
  }

  function tenantParams(extra = {}) {
    const fy = getFinYearId();
    return fy ? { ...extra, finYearId: fy } : extra;
  }

  const finYearId = useMemo(() => Number(getFinYearId() || 0), []);
  const finYearText = useMemo(() => localStorage.getItem("finYearText") || "", []);

  const [branchId, setBranchId] = useState("Select");
  const [departmentId, setDepartmentId] = useState("Select");
  const [designationId, setDesignationId] = useState("Select");
  const [schemeId, setSchemeId] = useState("Select");
  const [employeeCode, setEmployeeCode] = useState("");
  const [employeeName, setEmployeeName] = useState("");

  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [schemes, setSchemes] = useState([]);

  const [codeSuggestions, setCodeSuggestions] = useState([]);
  const [nameSuggestions, setNameSuggestions] = useState([]);

  const [records, setRecords] = useState([]);
  const [loadingInit, setLoadingInit] = useState(false);
  const [loading, setLoading] = useState(false);

  const [showFilters, setShowFilters] = useState(true);
  const [selectAll, setSelectAll] = useState(false);

  const recordsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [pendingDisableIds, setPendingDisableIds] = useState([]);

  const axiosWithFY = useMemo(() => {
    return axios.create({
      baseURL: BASE_URL,
      headers: tenantHeaders(),
      params: tenantParams(),
    });
  }, [finYearId]);

  const fetchInit = async () => {
    if (!finYearId && !finYearText) {
      toast.error("Financial year missing! Please login again.");
      return;
    }

    try {
      setLoadingInit(true);
      const res = await axiosWithFY.get("/api/payroll/scheme-employees/init");

      if (!res.data?.status) {
        toast.error(res.data?.message || "Failed to load init data");
        return;
      }

      const d = res.data.data || {};
      setDepartments(d.departments || []);
      setBranches(d.branches || []);
      setSchemes(d.schemes || []);
      setDesignations([]);
    } catch (err) {
      console.error("Init error:", err);
      toast.error(err.response?.data?.message || "Failed to load init data");
    } finally {
      setLoadingInit(false);
    }
  };

  const fetchDesignations = async () => {
    try {
      const res = await axiosWithFY.get("/api/payroll/scheme-employees/designations");

      if (!res.data?.status) {
        toast.error(res.data?.message || "Failed to load designations");
        return;
      }

      setDesignations(res.data.data || []);
    } catch (err) {
      console.error("Designations error:", err);
      toast.error(err.response?.data?.message || "Failed to load designations");
    }
  };

  const fetchList = async (page = 1) => {
    if (!finYearId && !finYearText) {
      toast.error("Financial year missing! Please login again.");
      return;
    }

    try {
      setLoading(true);

      const res = await axiosWithFY.get("/api/payroll/scheme-employees", {
        params: tenantParams({
          page,
          limit: recordsPerPage,
          branchId,
          employeeCode,
          employeeName,
          departmentId,
          designationId,
          schemeId,
        }),
      });

      if (!res.data?.status) {
        toast.error(res.data?.message || "Failed to load list");
        return;
      }

      const list = res.data.data || [];
      const mapped = list.map((row, idx) => ({
        id: row.fld_employeeid,
        sNo: (page - 1) * recordsPerPage + (idx + 1),
        employeeCode: row.fld_employeecode,
        employeeName: row.fld_employee,
        departmentName: row.fld_departmentname,
        designationName: row.fld_designationname,
        branchName: row.fld_branchname,
        schemeName: row.fld_schemename,
        selected: false,
      }));

      setRecords(mapped);
      setCurrentPage(res.data.page || page);
      setTotalPages(res.data.totalPages || 1);
      setTotalRecords(res.data.total || 0);
      setSelectAll(false);
    } catch (err) {
      console.error("List error:", err);
      toast.error(err.response?.data?.message || "Failed to load list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInit();
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDepartmentChange = async (val) => {
    setDepartmentId(val);
    setDesignationId("Select");
    await fetchDesignations();
  };

  const handleSelectRecord = (id, checked) => {
    setRecords((prev) => {
      const next = prev.map((r) => (r.id === id ? { ...r, selected: checked } : r));
      const allChecked = next.length > 0 && next.every((x) => x.selected);
      setSelectAll(allChecked);
      return next;
    });
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    setRecords((prev) => prev.map((r) => ({ ...r, selected: checked })));
  };

  const openDisableConfirm = () => {
    const selectedIds = records.filter((r) => r.selected).map((r) => r.id);

    if (selectedIds.length === 0) {
      toast.error("Select any one employee");
      return;
    }

    setPendingDisableIds(selectedIds);
    setConfirmOpen(true);
  };

  const closeDisableConfirm = () => {
    if (isDisabling) return;
    setConfirmOpen(false);
    setPendingDisableIds([]);
  };

  const confirmDisableSelected = async () => {
    if (pendingDisableIds.length === 0) return;

    try {
      setIsDisabling(true);

      const res = await axiosWithFY.post("/api/payroll/scheme-employees/disable", {
        employeeIds: pendingDisableIds,
      });

      if (res.data?.status) {
        toast.success(res.data?.message || "Payroll Scheme Disabled Successfully");
        setConfirmOpen(false);
        setPendingDisableIds([]);
        await fetchList(currentPage);
      } else {
        toast.error(res.data?.message || "Payroll Scheme Disabled Failed");
      }
    } catch (err) {
      console.error("Disable error:", err);
      toast.error(err.response?.data?.message || "Server error");
    } finally {
      setIsDisabling(false);
    }
  };

  const handleSearch = async () => {
    setCurrentPage(1);
    await fetchList(1);
  };

  const fetchCodeSuggestions = async (txt) => {
    const t = String(txt || "").trim();
    if (t.length < 1) {
      setCodeSuggestions([]);
      return;
    }
    try {
      const res = await axiosWithFY.get("/api/payroll/scheme-employees/autocomplete/code", {
        params: tenantParams({ prefix: t }),
      });
      if (res.data?.status) setCodeSuggestions(res.data.data || []);
    } catch (e) {}
  };

  const fetchNameSuggestions = async (txt) => {
    const t = String(txt || "").trim();
    if (t.length < 1) {
      setNameSuggestions([]);
      return;
    }
    try {
      const res = await axiosWithFY.get("/api/payroll/scheme-employees/autocomplete/name", {
        params: tenantParams({ prefix: t }),
      });
      if (res.data?.status) setNameSuggestions(res.data.data || []);
    } catch (e) {}
  };

  const handlePageChange = (page) => {
    if (!page || page === "..." || page === currentPage) return;
    fetchList(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = 1; i <= Math.min(maxVisiblePages, totalPages); i++) pages.push(i);
      if (totalPages > maxVisiblePages) pages.push("...");
    }
    return pages;
  };

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PAYROLL SCHEME EMPLOYEES DISABLE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Payroll Configuration Scheme Listing
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {totalRecords} Records
              </span>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => navigate("/adminMaster/PayrollSchemeSelectEmployeesEntry")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          <div className={`rounded-xl border mb-6 overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {showFilters && (
              <div className={`p-6 ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                {loadingInit ? (
                  <div className={`${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading filters...</div>
                ) : (
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-12 md:col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Branch Name
                      </label>
                      <select
                        value={branchId}
                        onChange={(e) => setBranchId(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                        }`}
                      >
                        <option value="Select">Select</option>
                        {branches.map((b) => (
                          <option key={b.fld_Branchid ?? b.id ?? b.value} value={b.fld_branchid ?? b.id ?? b.value}>
                            {b.fld_BranchName ?? b.name ?? b.text}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-12 md:col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Employee Code
                      </label>
                      <input
                        value={employeeCode}
                        onChange={(e) => {
                          setEmployeeCode(e.target.value);
                          fetchCodeSuggestions(e.target.value);
                        }}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                        }`}
                        placeholder="Type code..."
                        list="empCodeList"
                      />
                      <datalist id="empCodeList">
                        {codeSuggestions.map((s, idx) => (
                          <option key={idx} value={s} />
                        ))}
                      </datalist>
                    </div>

                    <div className="col-span-12 md:col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Employee Name
                      </label>
                      <input
                        value={employeeName}
                        onChange={(e) => {
                          setEmployeeName(e.target.value);
                          fetchNameSuggestions(e.target.value);
                        }}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                        }`}
                        placeholder="Type name..."
                        list="empNameList"
                      />
                      <datalist id="empNameList">
                        {nameSuggestions.map((s, idx) => (
                          <option key={idx} value={s} />
                        ))}
                      </datalist>
                    </div>

                    <div className="col-span-12 md:col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Department
                      </label>
                      <select
                        value={departmentId}
                        onChange={(e) => handleDepartmentChange(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                        }`}
                      >
                        <option value="Select">Select</option>
                        {departments.map((d) => (
                          <option key={d.fld_deptid} value={d.fld_deptid}>
                            {d.fld_departmentname}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-12 md:col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Designation
                      </label>
                      <select
                        value={designationId}
                        onChange={(e) => setDesignationId(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                        }`}
                      >
                        <option value="Select">Select</option>
                        {designations.map((d) => (
                          <option key={d.fld_desigid} value={d.fld_desigid}>
                            {d.fld_designationname}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-12 md:col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Scheme
                      </label>
                      <select
                        value={schemeId}
                        onChange={(e) => setSchemeId(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                        }`}
                      >
                        <option value="Select">Select</option>
                        {schemes.map((s) => (
                          <option key={s.fld_schemeid ?? s.id ?? s.value} value={s.fld_schemeid ?? s.id ?? s.value}>
                            {s.fld_schemename ?? s.name ?? s.text}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-12 flex justify-end">
                      <button
                        onClick={handleSearch}
                        className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                      >
                        <FaSearch />
                        Search
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">S.No</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Employee Code</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Employee Name</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Department</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Designation</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Branch</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Scheme</th>
                    <th className="px-4 py-4 text-center font-semibold">
                      <div className="flex items-center justify-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="w-4 h-4 cursor-pointer"
                        />
                        <span>Select All</span>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className={`px-4 py-6 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        Loading...
                      </td>
                    </tr>
                  ) : records.length === 0 ? (
                    <tr>
                      <td colSpan="8" className={`px-4 py-6 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    records.map((r, idx) => (
                      <tr
                        key={r.id}
                        className={`transition-colors duration-200 ${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                            ? "bg-gray-900"
                            : "bg-white"
                        }`}
                      >
                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.sNo}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.employeeCode}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.employeeName}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.departmentName}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.designationName}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.branchName}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.schemeName}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={!!r.selected}
                            onChange={(e) => handleSelectRecord(r.id, e.target.checked)}
                            className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center p-4">
              <div className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm font-medium`}>
                Total: {totalRecords} | Page: {currentPage} / {totalPages}
              </div>

              <div className="flex gap-1">
                {renderPageNumbers().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => handlePageChange(page)}
                    disabled={page === "..."}
                    className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                      page === currentPage
                        ? "bg-blue-600 text-white border-blue-600"
                        : page === "..."
                        ? isDark
                          ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                          : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                        : isDark
                        ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={openDisableConfirm}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark ? "bg-red-700 hover:bg-red-600 text-white" : "bg-red-600 hover:bg-red-700 text-white"
              }`}
            >
              <FaTrash />
              Disable Scheme
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="disable-confirm-title"
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeDisableConfirm}
          />

          <div
            className={`relative w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-gray-900 to-gray-800 border-white/10 text-white"
                : "bg-white border-gray-200 text-gray-900"
            }`}
          >
            <div className={`h-1.5 w-full ${buttonClass}`} />

            <button
              type="button"
              onClick={closeDisableConfirm}
              className={`absolute top-3 right-3 p-1.5 rounded-lg transition ${
                isDark ? "hover:bg-white/10 text-white/70" : "hover:bg-gray-100 text-gray-500"
              }`}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-600/15 text-red-500">
                  <FaExclamationTriangle className="text-xl" />
                </div>
                <div>
                  <h3 id="disable-confirm-title" className="text-lg font-semibold">
                    Disable Payroll Scheme
                  </h3>
                  <p className={`mt-2 text-sm leading-6 ${isDark ? "text-white/70" : "text-gray-600"}`}>
                    Are you sure you want to disable scheme for{" "}
                    <span className="font-semibold">
                      {pendingDisableIds.length} employee{pendingDisableIds.length > 1 ? "s" : ""}
                    </span>
                    ? This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeDisableConfirm}
                  disabled={isDisabling}
                  className={`px-4 py-2 rounded-xl text-sm font-medium border transition ${
                    isDark
                      ? "border-white/15 bg-white/5 hover:bg-white/10 text-white"
                      : "border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
                  } disabled:opacity-50`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDisableSelected}
                  disabled={isDisabling}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 shadow transition hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  <FaTrash />
                  {isDisabling ? "Disabling..." : "Disable Scheme"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayoutWrapper>
  );
};

export default PayrollSchemeSelectEmployees;